import type { ISceneObj } from '../../../types/SceneType';

/**
 * Centralized duplicate scene detection and management utilities
 */

export interface DuplicateInfo {
	isOriginal: boolean;
	originalName: string;
	originalScene: ISceneObj;
	duplicateCount: number;
}

export interface SceneContentSignature {
	if: any[];
	do: any[];
	infinno?: any;
}

/**
 * Creates a unique signature for a scene based on its content
 * Used to identify duplicate scenes regardless of metadata differences
 */
export function getSceneContentSignature(sceneObj: ISceneObj): string {
	const content: SceneContentSignature = {
		if: sceneObj.if?.and || sceneObj.if?.or || [],
		do: sceneObj.do || [],
	};

	if ('infinno' in sceneObj && (sceneObj as any).infinno) {
		content.infinno = (sceneObj as any).infinno;
	}

	// Sort arrays for consistent comparison
	if (Array.isArray(content.if) && content.if.length > 0) {
		content.if = content.if.sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
	}
	if (content.do.length > 0) {
		content.do = content.do.sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
	}

	// Create byte-level signature for better uniqueness
	const jsonString = JSON.stringify(content);
	const encoder = new TextEncoder();
	const bytes = encoder.encode(jsonString);

	// Create a hash-like signature from bytes
	let signature = '';
	for (let i = 0; i < bytes.length; i++) {
		signature += bytes[i].toString(16).padStart(2, '0');
	}

	return signature;
}

/**
 * Detects if a scene is a duplicate and returns information about the original
 */
export function isDuplicateScene(
	sceneId: string | number,
	allScenes: Record<string, ISceneObj>,
): DuplicateInfo | null {
	const scene = allScenes[sceneId];
	if (!scene) return null;

	// Create signature for current scene
	const currentSignature = getSceneContentSignature(scene);

	// Find all scenes with the same signature
	const duplicateScenes = Object.values(allScenes).filter(
		(s: ISceneObj) => getSceneContentSignature(s) === currentSignature,
	);

	if (duplicateScenes.length <= 1) return null;

	// Sort by creation timestamp to find original
	const sortedScenes = sortScenesByCreatedTime(duplicateScenes);

	const originalScene = sortedScenes[0];

	// Return info if current scene is not the original
	if (originalScene._id !== scene._id) {
		return {
			isOriginal: false,
			originalName: originalScene._meta?.name || 'Unknown Scene',
			originalScene,
			duplicateCount: duplicateScenes.length,
		};
	}

	return null;
}

/**
 * Groups scenes by their content signature
 * Returns a map where each key is a signature and value is array of scenes with that signature
 */
function groupScenesBySignature(
	scenes: Record<string, ISceneObj> | ISceneObj[],
): Map<string, ISceneObj[]> {
	const signatureGroups = new Map<string, ISceneObj[]>();
	const scenesList = Array.isArray(scenes) ? scenes : Object.values(scenes);

	scenesList.forEach((scene) => {
		const signature = getSceneContentSignature(scene);
		if (!signatureGroups.has(signature)) {
			signatureGroups.set(signature, []);
		}
		signatureGroups.get(signature)!.push(scene);
	});

	return signatureGroups;
}

/**
 * Finds all duplicate scene groups (groups with more than one scene)
 * Returns array of duplicate groups, each containing scenes with identical content
 */
export function findDuplicateSceneGroups(
	scenes: Record<string, ISceneObj> | ISceneObj[],
): ISceneObj[][] {
	const signatureGroups = groupScenesBySignature(scenes);
	return Array.from(signatureGroups.values())
		.filter((group) => group.length > 1)
		.map((group) => sortScenesByCreatedTime(group));
}

/**
 * Gets all scenes that are duplicates (not originals) from a scenes collection
 */
export function getAllDuplicateScenes(scenes: Record<string, ISceneObj>): ISceneObj[] {
	const duplicateGroups = findDuplicateSceneGroups(scenes);
	const duplicates: ISceneObj[] = [];

	duplicateGroups.forEach((group) => {
		// First scene in sorted group is original, rest are duplicates
		duplicates.push(...group.slice(1));
	});

	return duplicates;
}

/**
 * Gets the original scene for a given duplicate scene
 */
export function getOriginalScene(
	sceneId: string | number,
	allScenes: Record<string, ISceneObj>,
): ISceneObj | null {
	const duplicateInfo = isDuplicateScene(sceneId, allScenes);
	return duplicateInfo?.originalScene || null;
}

/**
 * Checks if two scenes have identical content (are duplicates)
 */
export function areScenesDuplicates(scene1: ISceneObj, scene2: ISceneObj): boolean {
	return getSceneContentSignature(scene1) === getSceneContentSignature(scene2);
}

/**
 * Gets duplicate information for a specific scene (used in SceneDrawer)
 * Returns the duplicate info if the scene is a duplicate, otherwise null
 */
export function getDuplicateInfo(
	scene: ISceneObj,
	allScenes: Record<string, ISceneObj>,
): DuplicateInfo | null {
	if (!scene || !scene._id) return null;
	return isDuplicateScene(scene._id, allScenes);
}

/**
 * Generates a smart copy name for scene duplication
 * Handles naming like: "test" -> "test - Copy" -> "test - Copy #2" -> "test - Copy #3"
 */
export function generateCopyName(
	originalSceneName: string,
	allScenes: Record<string, ISceneObj>,
): string {
	// Extract the base name (remove existing " - Copy" or " - Copy #X" suffixes)
	const baseName = getBaseName(originalSceneName);

	// Find all scenes that start with this base name and are copies
	const existingCopyNames = Object.values(allScenes)
		.map((scene) => scene._meta?.name || '')
		.filter((name) => {
			// Match exact pattern: baseName - Copy or baseName - Copy #X
			return (
				name === `${baseName} - Copy` ||
				name.match(new RegExp(`^${escapeRegExp(baseName)} - Copy #\\d+$`))
			);
		})
		.sort();

	// If no copies exist, create the first copy
	if (existingCopyNames.length === 0) {
		return `${baseName} - Copy`;
	}

	// Find the highest copy number
	let maxCopyNumber = 0;

	existingCopyNames.forEach((name) => {
		if (name === `${baseName} - Copy`) {
			// This is the first copy (implicitly #1)
			maxCopyNumber = Math.max(maxCopyNumber, 1);
		} else {
			// Check for numbered copies like " - Copy #2", " - Copy #3", etc.
			const numberMatch = name.match(new RegExp(`^${escapeRegExp(baseName)} - Copy #(\\d+)$`));
			if (numberMatch) {
				const copyNumber = parseInt(numberMatch[1], 10);
				maxCopyNumber = Math.max(maxCopyNumber, copyNumber);
			}
		}
	});

	// Generate the next copy name
	const nextNumber = maxCopyNumber + 1;

	// If this would be copy #2, but we don't have an unnumbered "Copy",
	// then create the first unnumbered copy
	if (nextNumber === 2 && !existingCopyNames.includes(`${baseName} - Copy`)) {
		return `${baseName} - Copy`;
	}

	return `${baseName} - Copy #${nextNumber}`;
}

/**
 * Extracts the base name from a scene name, removing copy suffixes
 * Examples:
 * "test" -> "test"
 * "test - Copy" -> "test"
 * "test - Copy #2" -> "test"
 * "test - Copy #15" -> "test"
 */
function getBaseName(sceneName: string): string {
	// Remove " - Copy #X" pattern
	let baseName = sceneName.replace(/ - Copy #\d+$/, '');

	// Remove " - Copy" pattern
	baseName = baseName.replace(/ - Copy$/, '');

	return baseName;
}

/**
 * Escapes special regex characters in a string
 */
function escapeRegExp(string: string): string {
	return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Sorts scenes by creation timestamp (oldest first)
 */
export function sortScenesByCreatedTime(scenes: ISceneObj[]): ISceneObj[] {
	return scenes.sort(
		(a, b) => ((a as any)._meta?.created_ts || 0) - ((b as any)._meta?.created_ts || 0),
	);
}

/**
 * Generates copy labels for a list of scenes
 * Returns an object mapping scene IDs to arrays of copy label information
 */
function generateCopyLabels(
	scenes: ISceneObj[],
	createLabelFunction: (originalName: string) => any,
): Record<string | number, any[]> {
	const labels: Record<string | number, any[]> = {};

	// Group scenes by their content signature
	const duplicateGroups = findDuplicateSceneGroups(scenes);

	// Process each duplicate group
	duplicateGroups.forEach((group) => {
		if (group.length > 1) {
			// First scene in sorted group is original, rest are copies
			const originalScene = group[0];
			const copies = group.slice(1);

			// Add copy labels to all scenes except the original
			copies.forEach((copyScene) => {
				if (!labels[copyScene._id]) {
					labels[copyScene._id] = [];
				}

				const copyLabel = createLabelFunction(originalScene._meta?.name || 'Unknown Scene');

				labels[copyScene._id].push(copyLabel);
			});
		}
	});

	return labels;
}

/**
 * Updates copy labels for scenes, preserving existing non-copy labels
 * Returns updated labels object with copy labels added/updated/removed as needed
 */
export function updateCopyLabels(
	scenes: ISceneObj[],
	existingLabels: Record<string | number, any[]>,
	createLabelFunction: (originalName: string) => any,
	copyLabelId: number = 2,
): Record<string | number, any[]> {
	const newLabels = { ...existingLabels };

	// Generate fresh copy labels
	const copyLabels = generateCopyLabels(scenes, createLabelFunction);

	// Update labels for all scenes
	scenes.forEach((scene) => {
		const sceneId = scene._id;

		// Initialize if needed
		if (!newLabels[sceneId]) {
			newLabels[sceneId] = [];
		}

		// Remove existing copy labels
		newLabels[sceneId] = newLabels[sceneId].filter((label) => label.id !== copyLabelId);

		// Add new copy labels if scene is a copy
		if (copyLabels[sceneId]) {
			newLabels[sceneId] = [...newLabels[sceneId], ...copyLabels[sceneId]];
		}

		// Clean up empty label arrays
		if (newLabels[sceneId].length === 0) {
			delete newLabels[sceneId];
		}
	});

	return newLabels;
}
